import {
  RESET_BACKDROP_STORE,
  RESET_CALCULATOR_ROUNDING,
  RESET_CALCULATOR_ROUNDING_PUBLIC_RESPONSE,
  RESET_CALCULATOR_ROUNDING_RESPONSE,
  RESET_REGISTER_INFORMATION_STORE,
} from 'constants';
import { createContext, useReducer } from 'react';

import {
  RESET_ADD_TRANSACTION_INFORMATION_STORE,
  RESET_EDIT_RECEIVER_INFORMATION_STORE,
  RESET_PORTAL_MODAL_STORE,
} from 'constants';
import { RESET_SIGNUP_PII_INFORMATION } from 'features/pii/const';
import { RESET_FORM_ADD_PAYTO_ACCOUNT_STORE } from 'features/to/const';
import {
  ServiceAlertsByCountryCodeDefault,
  ServiceAlertsByPaymentsDefault,
} from 'hooks/service-alerts/model/ServiceAlertsLocalStore';
import {
  SET_ACCOUNT_AGREEMENT_EDITING,
  SET_ADD_PAYTO_ACCOUNT_INFORMATION,
  SET_ADD_RECEIVER_INFORMATION,
  SET_ADD_TRANSACTION_CURRENT_STEP,
  SET_ADD_TRANSACTION_INFORMATION,
  SET_ALL_TRANSACTIONS_AS_CANCELLED,
  SET_CALCULATOR_ROUNDING,
  SET_CALULATOR_ROUNDING_PUBLIC_RESPONSE,
  SET_CALULATOR_ROUNDING_RESPONSE,
  SET_CONFIG_OCCUPATION,
  SET_CONFIG_PURPOSE,
  SET_CONFIG_PURPOSE_OF_BUSINES,
  SET_CONFIG_RELATIONSHIP,
  SET_CONFIG_RELATIONSHIP_OF_BUSINESS,
  SET_CONFIG_SOURCES_OF_WEALTH,
  SET_CURRENT_BUSINESS_RECEIVER,
  SET_CURRENT_NOTIFY_DATA,
  SET_CURRENT_RECEIVER,
  SET_CURRENT_USER,
  SET_EDIT_RECEIVER_CURRENT_STEP,
  SET_EDIT_RECEIVER_INFORMATION,
  SET_EVENTS,
  SET_EVENTS_ON_SIDEBAR,
  SET_EVENT_AVAILABLE,
  SET_EXCHANGE_RATES,
  SET_FORM_PROFILE_KYC_INFORMATION,
  SET_LOADING_MESSAGE,
  SET_MENU_EXPAND,
  SET_NEW_NOTIFICATION,
  SET_NOTIFICATIONS,
  SET_OPEN_REFRESH_TOKEN_POPUP,
  SET_PAYTO_ACCOUNTS_AGREEMENT,
  SET_PAYTO_ACCOUNT_AUTHORISING,
  SET_PAYTO_AMEND_LIMIT,
  SET_PAYTO_BANK_ACCOUNTS_VERIFIED,
  SET_PAYTO_BSB_ACCOUNT_CHECK_PENDING,
  SET_PAYTO_BSB_NUMBER_CHECK_PENDING,
  SET_PAYTO_CREATE_LIMIT,
  SET_PAYTO_MAX_AMOUNT_EDITED,
  SET_PAYTO_PAYID_BANK_VALIDATION_FAILURE,
  SET_PAYTO_PAYID_CHECK_PENDING,
  SET_PAYTO_PAYID_GENERATION,
  SET_PORTAL_MODAL,
  SET_PROFILE_OCCUPATION,
  SET_REFERRAL_PARTICIPANTS,
  SET_REFRESH_RECEIVER_LIST,
  SET_REFRESH_TRANSACTION_LIST,
  SET_REGISTER_INFORMATION,
  SET_REMITTANCE_RATES,
  SET_REMITTANCE_RATES_SELECTED,
  SET_SERVICE_ALERTS_BY_COUNTRY_CODE,
  SET_SERVICE_ALERTS_BY_PAYMENTS,
  SET_SHOW_BACKDROP,
  SET_SHOW_LOADING,
  SET_SHOW_PENDING,
  SET_SHOW_SEARCHING,
  SET_SIGNUP_PII_INFORMATION,
  SET_TOKEN_TRANSACTION,
  SET_TRANSACTIONS_COMPLETED_EMPTY,
  SET_TRANSACTIONS_EMPTY,
  SET_TRANSACTION_DETAILS,
} from './action';

const initialState = {
  isRefreshToken: false,
  backdrop: RESET_BACKDROP_STORE,
  isLoading: false,
  loadingMessage: '',
  isPending: false,
  isSearching: false,
  portalModal: RESET_PORTAL_MODAL_STORE,
  currentUser: null,
  notifications: null,
  exchangeRates: null,
  remittanceRates: null,
  remittanceRatesSelected: null,
  serviceAlertsByCountryCode: ServiceAlertsByCountryCodeDefault,
  serviceAlertsByPayments: ServiceAlertsByPaymentsDefault,
  isNewNotification: false,
  currentReceiver: null,
  currentBusinessReceiver: null,
  registerInformation: RESET_REGISTER_INFORMATION_STORE,
  editReceiverInformation: RESET_EDIT_RECEIVER_INFORMATION_STORE,
  editReceiverCurrentStep: 0,
  addTransactionInformation: RESET_ADD_TRANSACTION_INFORMATION_STORE,
  addTransactionCurrentStep: 0,
  // The step navigated away from on the last step change. Lets Step 2 detect a
  // return from Step 1 (index 0) and refresh the discount list per the PRD.
  addTransactionPreviousStep: 0,
  transactionDetails: null,
  tokenTransaction: '',
  configOccupation: null,
  configRelationship: null,
  configRelationshipOfBusiness: null,
  configPurpose: null,
  configPurposeOfBusiness: null,
  configSourcesOfWealth: null,
  eventAvailable: null,
  refreshReceiverList: null,
  refreshTransactionsList: true,
  isMenuExpand: true,
  events: null,
  eventsOnSidebar: null,
  referralParticipants: null,
  isTransactionsEmpty: false,
  isTransactionsCompletedEmpty: false,
  isAllTransactionsAsCancelled: false,
  paytoAccountInformation: RESET_FORM_ADD_PAYTO_ACCOUNT_STORE,
  paytoBankAccountsVerified: null,
  paytoAccountsAgreement: null,
  isPaytoMaxAmountEdited: false,
  paytoAccountAuthorising: null,
  isAccountAgreementEditing: false,
  paytoPayIdGeneration: 0,
  // DEV-1401: authoritative createPayToLimit for the Add PayTo (Create) flow,
  // shared across BSB and PayID -- null-equivalent (see
  // isPayToLimitNullEquivalent) until a validation response supplies one.
  // Reset alongside paytoAccountInformation (see usePaytoStore's
  // resetPaytoAccountInformation) so it cannot leak between BSB/PayID or
  // successive recipients.
  paytoCreateLimit: null,
  // DEV-1401: authoritative amendPayToLimit for the Add PayTo (Create) flow,
  // read from the same live validation response as paytoCreateLimit (see
  // withCheckBSBNumber.jsx / withCheckBsbAccount.jsx / withCheckMobileNumber.jsx).
  // Reset alongside paytoAccountInformation/paytoCreateLimit (see
  // usePaytoStore's resetPaytoAccountInformation) so it cannot leak between
  // BSB/PayID or successive recipients.
  paytoAmendLimit: null,
  // DEV-1401: per-call pending signals (see SET_PAYTO_*_CHECK_PENDING
  // above). Distinct from isLoading -- these track only whichever
  // validation call(s) can affect paytoCreateLimit/paytoAmendLimit, not any
  // concurrent unrelated request.
  paytoBsbNumberCheckPending: false,
  paytoBsbAccountCheckPending: false,
  paytoPayIdCheckPending: false,
  // DEV-1401: whether the current PayID field's validation message is a
  // known bank-validation failure (NFND/CLSD) or the unrecognised-resultCode
  // fallback -- drives the Create PayTo modal's separate PayID failure row,
  // distinct from paytoAccountValidation (a message string, not a signal).
  paytoPayIdBankValidationFailure: false,
  signupPIIInformation: RESET_SIGNUP_PII_INFORMATION,
  formProfileKYCInformation: null,
  profileOccupation: null,
  calculatorRounding: RESET_CALCULATOR_ROUNDING,
  calculatorRoundingPublicResponse: RESET_CALCULATOR_ROUNDING_PUBLIC_RESPONSE,
  calculatorRoundingResponse: RESET_CALCULATOR_ROUNDING_RESPONSE,
  currentNotifyData: null,
};

const store = createContext(initialState);
const { Provider } = store;
let stateStore;
let dispatchStore;

const StateProvider = ({ children }) => {
  let [state, dispatch] = useReducer((state, action) => {
    const { type, payload } = action;

    switch (type) {
      case SET_OPEN_REFRESH_TOKEN_POPUP:
        return {
          ...state,
          isRefreshToken: payload,
        };
      case SET_SHOW_LOADING:
        return {
          ...state,
          isLoading: payload,
        };
      case SET_LOADING_MESSAGE:
        return {
          ...state,
          loadingMessage: payload,
        };
      case SET_SHOW_PENDING:
        return {
          ...state,
          isPending: payload,
        };
      case SET_SHOW_SEARCHING:
        return {
          ...state,
          isSearching: payload,
        };
      case SET_PORTAL_MODAL:
        return {
          ...state,
          portalModal: payload,
        };
      case SET_SHOW_BACKDROP:
        return {
          ...state,
          backdrop: {
            ...state.backdrop,
            ...payload,
          },
        };
      case SET_CURRENT_USER:
        return { ...state, currentUser: payload };
      case SET_EXCHANGE_RATES:
        return { ...state, exchangeRates: payload };
      case SET_REMITTANCE_RATES:
        return { ...state, remittanceRates: payload };
      case SET_REMITTANCE_RATES_SELECTED:
        return { ...state, remittanceRatesSelected: payload };
      case SET_SERVICE_ALERTS_BY_COUNTRY_CODE:
        return { ...state, serviceAlertsByCountryCode: payload };
      case SET_SERVICE_ALERTS_BY_PAYMENTS:
        return { ...state, serviceAlertsByPayments: payload };
      case SET_NOTIFICATIONS:
        return { ...state, notifications: payload };
      case SET_NEW_NOTIFICATION:
        return { ...state, isNewNotification: payload };
      case SET_CURRENT_RECEIVER:
        return { ...state, currentReceiver: payload };
      case SET_CURRENT_BUSINESS_RECEIVER:
        return { ...state, currentBusinessReceiver: payload };
      case SET_REGISTER_INFORMATION:
        return {
          ...state,
          registerInformation: { ...state.registerInformation, ...payload },
        };
      case SET_EDIT_RECEIVER_INFORMATION:
        return {
          ...state,
          editReceiverInformation: {
            ...state.editReceiverInformation,
            ...payload,
          },
        };
      case SET_ADD_RECEIVER_INFORMATION:
        return {
          ...state,
          editReceiverInformation: {
            ...state.editReceiverInformation,
            ...payload,
          },
        };
      case SET_EDIT_RECEIVER_CURRENT_STEP:
        return {
          ...state,
          editReceiverCurrentStep: payload,
        };
      case SET_ADD_TRANSACTION_INFORMATION:
        return {
          ...state,
          addTransactionInformation: {
            ...state.addTransactionInformation,
            ...payload,
          },
        };
      case SET_ADD_TRANSACTION_CURRENT_STEP:
        return {
          ...state,
          addTransactionPreviousStep: state.addTransactionCurrentStep,
          addTransactionCurrentStep: payload,
        };
      case SET_TRANSACTION_DETAILS:
        return {
          ...state,
          transactionDetails: payload,
        };
      case SET_TOKEN_TRANSACTION:
        return {
          ...state,
          tokenTransaction: payload,
        };
      case SET_CONFIG_OCCUPATION:
        return {
          ...state,
          configOccupation: payload,
        };
      case SET_CONFIG_RELATIONSHIP:
        return {
          ...state,
          configRelationship: payload,
        };
      case SET_CONFIG_RELATIONSHIP_OF_BUSINESS:
        return {
          ...state,
          configRelationshipOfBusiness: payload,
        };
      case SET_CONFIG_PURPOSE:
        return {
          ...state,
          configPurpose: payload,
        };
      case SET_CONFIG_PURPOSE_OF_BUSINES:
        return {
          ...state,
          configPurposeOfBusiness: payload,
        };
      case SET_CONFIG_SOURCES_OF_WEALTH:
        return {
          ...state,
          configSourcesOfWealth: payload,
        };
      case SET_EVENT_AVAILABLE:
        return {
          ...state,
          eventAvailable: payload,
        };
      case SET_REFRESH_RECEIVER_LIST:
        return { ...state, refreshReceiverList: payload };
      case SET_REFRESH_TRANSACTION_LIST:
        return { ...state, refreshTransactionsList: payload };
      case SET_MENU_EXPAND:
        return {
          ...state,
          isMenuExpand: payload,
        };
      case SET_EVENTS:
        return {
          ...state,
          events: payload,
        };
      case SET_EVENTS_ON_SIDEBAR:
        return {
          ...state,
          eventsOnSidebar: payload,
        };
      case SET_REFERRAL_PARTICIPANTS:
        return {
          ...state,
          referralParticipants: payload,
        };
      case SET_TRANSACTIONS_EMPTY:
        return {
          ...state,
          isTransactionsEmpty: payload,
        };
      case SET_TRANSACTIONS_COMPLETED_EMPTY:
        return {
          ...state,
          isTransactionsCompletedEmpty: payload,
        };
      case SET_ALL_TRANSACTIONS_AS_CANCELLED:
        return {
          ...state,
          isAllTransactionsAsCancelled: payload,
        };
      case SET_ADD_PAYTO_ACCOUNT_INFORMATION:
        return {
          ...state,
          paytoAccountInformation: payload,
        };
      case SET_PAYTO_BANK_ACCOUNTS_VERIFIED:
        return {
          ...state,
          paytoBankAccountsVerified: payload,
        };
      case SET_PAYTO_ACCOUNTS_AGREEMENT:
        return {
          ...state,
          paytoAccountsAgreement: payload,
        };
      case SET_PAYTO_MAX_AMOUNT_EDITED:
        return {
          ...state,
          isPaytoMaxAmountEdited: payload,
        };
      case SET_PAYTO_ACCOUNT_AUTHORISING:
        return {
          ...state,
          paytoAccountAuthorising: payload,
        };
      case SET_ACCOUNT_AGREEMENT_EDITING:
        return {
          ...state,
          isAccountAgreementEditing: payload,
        };
      case SET_PAYTO_PAYID_GENERATION:
        return {
          ...state,
          paytoPayIdGeneration: payload,
        };
      case SET_PAYTO_CREATE_LIMIT:
        return {
          ...state,
          paytoCreateLimit: payload,
        };
      case SET_PAYTO_AMEND_LIMIT:
        return {
          ...state,
          paytoAmendLimit: payload,
        };
      case SET_PAYTO_BSB_NUMBER_CHECK_PENDING:
        return {
          ...state,
          paytoBsbNumberCheckPending: payload,
        };
      case SET_PAYTO_BSB_ACCOUNT_CHECK_PENDING:
        return {
          ...state,
          paytoBsbAccountCheckPending: payload,
        };
      case SET_PAYTO_PAYID_CHECK_PENDING:
        return {
          ...state,
          paytoPayIdCheckPending: payload,
        };
      case SET_PAYTO_PAYID_BANK_VALIDATION_FAILURE:
        return {
          ...state,
          paytoPayIdBankValidationFailure: payload,
        };
      case SET_SIGNUP_PII_INFORMATION:
        return {
          ...state,
          signupPIIInformation: payload,
        };
      case SET_FORM_PROFILE_KYC_INFORMATION:
        return {
          ...state,
          formProfileKYCInformation: payload,
        };
      case SET_PROFILE_OCCUPATION:
        return {
          ...state,
          profileOccupation: payload,
        };
      case SET_CALCULATOR_ROUNDING:
        return {
          ...state,
          calculatorRounding: payload,
        };
      case SET_CALULATOR_ROUNDING_PUBLIC_RESPONSE:
        return {
          ...state,
          calculatorRoundingPublicResponse: payload,
        };
      case SET_CALULATOR_ROUNDING_RESPONSE:
        return {
          ...state,
          calculatorRoundingResponse: payload,
        };
      case SET_CURRENT_NOTIFY_DATA:
        return {
          ...state,
          currentNotifyData: payload,
        };
      default:
        return state;
    }
  }, initialState);

  stateStore = state;
  dispatchStore = dispatch;

  return <Provider value={{ state, dispatch }}>{children}</Provider>;
};

export { StateProvider, dispatchStore, stateStore, store };
